import { motion } from 'framer-motion';
import { FiArrowUpRight, FiBriefcase, FiCode } from 'react-icons/fi';
import experience from '../../data/journey';
import GlassCard from '../shared/GlassCard';
import './Journey.css';

export default function Journey() {
  return (
    <section id="experience" className="relative overflow-hidden bg-white px-6 py-20 dark:bg-gray-900 md:px-20">
      <div className="pointer-events-none absolute -left-40 top-24 h-80 w-80 rounded-full bg-indigo-300/20 blur-3xl dark:bg-indigo-900/20" />
      <div className="relative mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
        >
          <p className="section-eyebrow">Where I’ve contributed</p>
          <h2 className="mb-4 text-center text-4xl font-bold text-gray-900 dark:text-white">
            <span className="border-b-4 border-indigo-500 pb-1">Experience</span>
          </h2>
          <p className="mx-auto mb-12 max-w-2xl text-center text-gray-600 dark:text-gray-300">
            From collaborating on product work in a team to delivering a full-stack solution for a client.
          </p>

          <div className="experience-timeline mx-auto max-w-5xl space-y-7">
            {experience.map((item, index) => {
              const Icon = index === 0 ? FiBriefcase : FiCode;

              return (
                <motion.article
                  key={`${item.organization}-${item.role}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -18 : 18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.12 }}
                  viewport={{ once: true, amount: 0.15 }}
                  className="experience-item grid gap-3 md:grid-cols-[190px_42px_minmax(0,1fr)] md:gap-4"
                >
                  <div className="flex items-start justify-between gap-3 pt-2 md:flex-col md:items-end md:text-right">
                    <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">{item.period}</span>
                    <span className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-300">{item.type}</span>
                  </div>

                  <div className="experience-marker relative hidden justify-center md:flex" aria-hidden="true">
                    <span className="relative z-10 mt-1 flex h-10 w-10 items-center justify-center rounded-2xl border border-indigo-200 bg-white text-indigo-600 shadow-sm dark:border-indigo-700 dark:bg-gray-950 dark:text-indigo-300">
                      <Icon size={18} />
                    </span>
                  </div>

                  <GlassCard className="p-5 transition duration-300 hover:-translate-y-1 hover:border-indigo-300/60 hover:shadow-xl dark:hover:border-indigo-500/40 sm:p-7">
                    <div className="mb-4 flex items-start gap-3">
                      <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300 md:hidden">
                        <Icon size={18} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">{item.role}</h3>
                        <p className="mt-1 font-medium text-indigo-700 dark:text-indigo-300">{item.organization}</p>
                      </div>
                      {item.link && (
                        <a href={item.link} target="_blank" rel="noopener noreferrer" className="hidden items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:text-indigo-300 dark:hover:bg-gray-800 sm:inline-flex">
                          {item.linkLabel}<FiArrowUpRight aria-hidden="true" />
                        </a>
                      )}
                    </div>

                    <p className="mb-5 leading-relaxed text-gray-700 dark:text-gray-300">{item.summary}</p>

                    <ul className="grid gap-3 sm:grid-cols-2">
                      {item.highlights.map((highlight) => (
                        <li key={highlight} className="flex gap-2.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" aria-hidden="true" />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 flex flex-wrap gap-2 border-t border-gray-200/80 pt-5 dark:border-gray-700/80">
                      {item.focus.map((skill) => (
                        <span key={skill} className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">{skill}</span>
                      ))}
                    </div>

                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-1 rounded-lg text-sm font-semibold text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:text-indigo-300 sm:hidden">
                        {item.linkLabel}<FiArrowUpRight aria-hidden="true" />
                      </a>
                    )}
                  </GlassCard>
                </motion.article>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
